/**
 * Places a product's print areas on its pictures, for this reseller.
 *
 * What a proofsheet made from the PIM needs: for the picture of the ordered
 * colour, where each area is. The same product is photographed the same way
 * in every colour — picture 1 is the front, picture 2 the back — so a
 * placement is made once per picture number and serves every option; an
 * option whose photo was framed differently gets its own copy, edited apart.
 * Product pictures that belong to no option are placed on their own.
 *
 * A placement is four corners, not a rectangle: it can be turned and pulled
 * out of square to follow the product in the photo — a bag held at an angle,
 * the curve of a bottle. Corners are kept as fractions of the picture, which
 * is what lets one placement sit on pictures of different sizes; they are
 * written out in the pixels of the picture shown at save time, the PIM's
 * shape for them.
 */
define('pimPicturePositionerView',[
  'module',
  'marionette',
  'underscore',
  'jquery',
  'dialogView',
  'template!pimPicturePositionerTpl'
], function (
  Module,
  Marionette,
  _,
  $,
  DialogView,
  positionerTpl
) {
  'use strict';

  /** How many changes can be taken back. */
  var HISTORY_DEPTH = 100;

  /** Each zoom step, and how far it goes. */
  var ZOOM_STEP = 1.25;
  var ZOOM_MAX = 6;

  /** Distinct enough to tell areas apart on any product photo. */
  var COLOURS = ['#d92d20', '#1570ef', '#079455', '#dc6803', '#7a5af8', '#0e9384', '#c11574', '#4e5ba6'];

  Module.exports = DialogView.extend({
    template: positionerTpl,
    className: 'pim-picture-positioner',

    ui: {
      stage: '.js-stage',
      image: '.js-image',
      overlay: '.js-overlay',
      undo: '.js-undo',
      redo: '.js-redo',
      zoomLevel: '.js-zoom-level',
      loading: '.js-loading',
      strip: '.js-strip',
      scope: '.js-scope',
      error: '.js-error',
      save: '.js-save'
    },

    events: {
      'click .js-cancel-popup': 'hide',
      'click @ui.save': 'onSave',
      'click .js-area': 'onAreaClick',
      'click .js-lock': 'onLockClick',
      'click .js-ref': 'onRefClick',
      'click .js-preview': 'onPreviewClick',
      'click @ui.undo': 'undo',
      'click @ui.redo': 'redo',
      'click .js-zoom-in': 'onZoomIn',
      'click .js-zoom-out': 'onZoomOut',
      'click .js-zoom-fit': 'onZoomFit',
      'click .js-tab': 'onTab',
      'click .js-thumb': 'onThumb',
      'change .js-custom': 'onCustomChange'
    },

    /**
     * @param options.areas            [{code, name}]
     * @param options.options          [{pimId, code, label, pictures: [{url, supplierPositions}]}]
     *                                 pictures in view order (picture 1 first)
     * @param options.productPictures  [{url, supplierPositions}]
     * @param options.saved            the reseller's entries, PIM shape
     * @param options.start            optional {url} to open on
     */
    initialize: function (options) {
      this.areas = _.map(options.areas || [], function (area, index) {
        var w = parseFloat(area.width), h = parseFloat(area.height);
        return _.extend({
          colour: COLOURS[index % COLOURS.length],
          // Height over width of the printed area itself. A rectangle drawn
          // with these proportions is the area as it will be on the product.
          ratio: w > 0 && h > 0 ? h / w : null
        }, area);
      });
      // Areas whose proportions are not being kept. Kept by default: the
      // area is a fixed size on the product, and a box of another shape
      // would have the decoration land somewhere it cannot go.
      this.unlocked = {};
      this.options_ = options.options || [];
      this.productPictures = options.productPictures || [];
      this.viewCount = _.max([0].concat(_.map(this.options_, function (o) { return o.pictures.length; })));

      // Placements as {code: {x, y, w, h}} fractions of the picture.
      this.shared = {};       // by view
      this.overrides = {};    // by option pimId, then view
      this.product = {};      // by url
      this.loadSaved(options.saved || []);
      this.inheritFromSupplier();

      this.current = this.firstSelection(options.start);
      this.natural = null;

      // Every change goes through the history, so any of them can be taken
      // back: a slip of the hand on a placement that was right is the
      // commonest mistake here.
      this.undoStack = [];
      this.redoStack = [];
      // How large the picture is shown: 1 is "fits the window".
      this.zoom = 1;
      this.fitWidth = null;
    },

    // ------------------------------------------------------------- history

    /** Everything a placement is, as text, so states compare and copy cheaply. */
    snapshot: function () {
      return JSON.stringify({shared: this.shared, overrides: this.overrides, product: this.product});
    },

    /** Called before a change, with what things looked like until it. */
    commit: function (before) {
      before = before || this.snapshot();
      if (this.undoStack.length && this.undoStack[this.undoStack.length - 1] === before) {
        return;
      }
      this.undoStack.push(before);
      if (this.undoStack.length > HISTORY_DEPTH) {
        this.undoStack.shift();
      }
      this.redoStack = [];
      this.renderHistory();
    },

    restore: function (state) {
      var parsed = JSON.parse(state);
      this.shared = parsed.shared;
      this.overrides = parsed.overrides;
      this.product = parsed.product;
      this.renderStrip();
      this.renderScope();
      this.draw();
      this.renderHistory();
    },

    undo: function () {
      if (!this.undoStack.length) {
        return;
      }
      this.redoStack.push(this.snapshot());
      this.restore(this.undoStack.pop());
    },

    redo: function () {
      if (!this.redoStack.length) {
        return;
      }
      this.undoStack.push(this.snapshot());
      this.restore(this.redoStack.pop());
    },

    renderHistory: function () {
      this.ui.undo.prop('disabled', !this.undoStack.length);
      this.ui.redo.prop('disabled', !this.redoStack.length);
    },

    onKeydown: function (event) {
      var tag = (event.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        return;
      }
      var meta = event.ctrlKey || event.metaKey;
      if (meta && !event.shiftKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        this.undo();
      } else if (meta && (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'))) {
        event.preventDefault();
        this.redo();
      }
    },

    // ---------------------------------------------------------------- zoom

    /**
     * The picture shown larger than the window, scrolled around: a corner
     * cannot be put on the edge of a print at the size the whole product
     * fits in. Zooming keeps the point under the hand — or the middle of
     * the window — where it is.
     */
    setZoom: function (zoom, anchor) {
      zoom = Math.max(1, Math.min(ZOOM_MAX, zoom));
      if (!this.fitWidth) {
        return;
      }
      var $stage = this.ui.stage;
      var before = this.ui.image.width() || this.fitWidth;
      // The point to keep still, as a fraction of the picture, and where it
      // sits in the window.
      anchor = anchor || {x: $stage.width() / 2, y: $stage.height() / 2};
      var fx = ($stage.scrollLeft() + anchor.x) / before;
      var fy = ($stage.scrollTop() + anchor.y) / (this.ui.image.height() || 1);

      this.zoom = zoom;
      this.ui.image.css('width', Math.round(this.fitWidth * zoom));
      var after = this.ui.image.width();
      $stage.scrollLeft(fx * after - anchor.x);
      $stage.scrollTop(fy * this.ui.image.height() - anchor.y);
      this.ui.zoomLevel.text(Math.round(zoom * 100) + '%');
      this.draw();
    },

    onZoomIn: function () {
      this.setZoom(this.zoom * ZOOM_STEP);
    },

    onZoomOut: function () {
      this.setZoom(this.zoom / ZOOM_STEP);
    },

    onZoomFit: function () {
      this.setZoom(1);
    },

    onWheel: function (event) {
      var original = event.originalEvent || event;
      if (!(original.ctrlKey || original.metaKey)) {
        return;
      }
      event.preventDefault();
      var offset = this.ui.stage.offset();
      var anchor = {x: original.pageX - offset.left, y: original.pageY - offset.top};
      this.setZoom(this.zoom * (original.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), anchor);
    },

    // --------------------------------------------------------------- state

    loadSaved: function (entries) {
      _.each(entries, _.bind(function (entry) {
        var rects = this.toRects(entry.positions);
        if (entry.view === null || entry.view === undefined) {
          if (entry.imageUrl) {
            this.product[entry.imageUrl] = rects;
          }
        } else if (entry.optionPimId) {
          this.overrides[entry.optionPimId] = this.overrides[entry.optionPimId] || {};
          this.overrides[entry.optionPimId][entry.view] = rects;
        } else {
          this.shared[entry.view] = rects;
        }
      }, this));
    },

    /**
     * Where the reseller placed nothing, the supplier's placement is the
     * starting point: for a view, the first option that has one; for a
     * product picture, its own. Only until saved — saving writes it as the
     * reseller's.
     */
    inheritFromSupplier: function () {
      this.inherited = {};
      _.each(_.range(1, this.viewCount + 1), _.bind(function (view) {
        if (this.shared[view]) {
          return;
        }
        var from = _.find(this.options_, function (option) {
          var picture = option.pictures[view - 1];
          return picture && picture.supplierPositions && picture.supplierPositions.length;
        });
        if (from) {
          this.shared[view] = this.toRects(from.pictures[view - 1].supplierPositions);
          this.inherited['view:' + view] = true;
        }
      }, this));
      _.each(this.productPictures, _.bind(function (picture) {
        if (!this.product[picture.url] && picture.supplierPositions && picture.supplierPositions.length) {
          this.product[picture.url] = this.toRects(picture.supplierPositions);
          this.inherited['product:' + picture.url] = true;
        }
      }, this));
    },

    firstSelection: function (start) {
      if (start && start.url) {
        var product = _.findWhere(this.productPictures, {url: start.url});
        if (product) {
          return {kind: 'product', url: start.url};
        }
        for (var i = 0; i < this.options_.length; i++) {
          var at = _.indexOf(_.pluck(this.options_[i].pictures, 'url'), start.url);
          if (at >= 0) {
            return {kind: 'view', view: at + 1, optionPimId: this.options_[i].pimId};
          }
        }
      }
      if (this.viewCount > 0) {
        return {kind: 'view', view: 1, optionPimId: this.options_[0].pimId};
      }
      if (this.productPictures.length > 0) {
        return {kind: 'product', url: this.productPictures[0].url};
      }
      return null;
    },

    /** The rectangles being edited for the current selection. */
    rects: function () {
      var c = this.current;
      if (!c) {
        return {};
      }
      if (c.kind === 'product') {
        this.product[c.url] = this.product[c.url] || {};
        return this.product[c.url];
      }
      var own = this.overrides[c.optionPimId] && this.overrides[c.optionPimId][c.view];
      if (own) {
        return own;
      }
      this.shared[c.view] = this.shared[c.view] || {};
      return this.shared[c.view];
    },

    hasOverride: function (optionPimId, view) {
      return !!(this.overrides[optionPimId] && this.overrides[optionPimId][view]);
    },

    currentUrl: function () {
      var c = this.current;
      if (!c) {
        return null;
      }
      if (c.kind === 'product') {
        return c.url;
      }
      var option = _.findWhere(this.options_, {pimId: c.optionPimId});
      var picture = option && option.pictures[c.view - 1];
      return picture ? picture.url : null;
    },

    // ------------------------------------------------------------ geometry
    //
    // A shape is {p: [[x, y] × 4]} — top-left, top-right, bottom-right,
    // bottom-left of the *area*, wherever those corners landed on the
    // picture — in fractions of the picture's width and height.

    toRects: function (positions) {
      var rects = {};
      _.each(positions || [], function (p) {
        if (!p || !p.paCode || !p.basedWidth || !p.basedHeight ||
            p.coordTopLeftX === undefined || p.coordBottomRightX === undefined) {
          return;
        }
        var W = p.basedWidth, H = p.basedHeight;
        var corner = function (x, y, fallbackX, fallbackY) {
          return [(x === undefined || x === null ? fallbackX : x) / W,
                  (y === undefined || y === null ? fallbackY : y) / H];
        };
        rects[p.paCode] = {p: [
          corner(p.coordTopLeftX, p.coordTopLeftY, p.coordTopLeftX, p.coordTopLeftY),
          corner(p.coordTopRightX, p.coordTopRightY, p.coordBottomRightX, p.coordTopLeftY),
          corner(p.coordBottomRightX, p.coordBottomRightY, p.coordBottomRightX, p.coordBottomRightY),
          corner(p.coordBottomLeftX, p.coordBottomLeftY, p.coordTopLeftX, p.coordBottomRightY)
        ]};
      });
      return rects;
    },

    /** Written in the pixels of a picture of the given natural size. */
    toPositions: function (rects, natural) {
      return _.compact(_.map(this.areas, function (area) {
        var r = rects[area.code];
        if (!r) {
          return null;
        }
        var px = function (i) {
          return [Math.round(r.p[i][0] * natural.w), Math.round(r.p[i][1] * natural.h)];
        };
        var tl = px(0), tr = px(1), br = px(2), bl = px(3);
        return {
          paCode: area.code,
          basedWidth: natural.w,
          basedHeight: natural.h,
          coordTopLeftX: tl[0], coordTopLeftY: tl[1],
          coordTopRightX: tr[0], coordTopRightY: tr[1],
          coordBottomLeftX: bl[0], coordBottomLeftY: bl[1],
          coordBottomRightX: br[0], coordBottomRightY: br[1]
        };
      }));
    },

    /** An upright shape from a corner and a size, all fractions. (Not `box`: the dialog mixin owns that name.) */
    upright: function (x, y, w, h) {
      return {p: [[x, y], [x + w, y], [x + w, y + h], [x, y + h]]};
    },

    /** Fractions to the pixels the picture is shown at, and back. */
    toPx: function (r) {
      var W = this.ui.image.width(), H = this.ui.image.height();
      return _.map(r.p, function (c) { return [c[0] * W, c[1] * H]; });
    },

    fromPx: function (pts) {
      var W = this.ui.image.width(), H = this.ui.image.height();
      return {p: _.map(pts, function (c) { return [c[0] / W, c[1] / H]; })};
    },

    /** Whether every corner is on the picture. */
    inside: function (r) {
      return _.every(r.p, function (c) {
        return c[0] >= -1e-6 && c[1] >= -1e-6 && c[0] <= 1 + 1e-6 && c[1] <= 1 + 1e-6;
      });
    },

    /** Moved so that its bounding box is on the picture, if it can be. */
    nudgedInside: function (r) {
      var xs = _.map(r.p, function (c) { return c[0]; }), ys = _.map(r.p, function (c) { return c[1]; });
      var dx = 0, dy = 0;
      if (_.min(xs) < 0) { dx = -_.min(xs); } else if (_.max(xs) > 1) { dx = 1 - _.max(xs); }
      if (_.min(ys) < 0) { dy = -_.min(ys); } else if (_.max(ys) > 1) { dy = 1 - _.max(ys); }
      return {p: _.map(r.p, function (c) { return [c[0] + dx, c[1] + dy]; })};
    },

    /** Twice the signed area in pixels; zero means the corners fell on a line. */
    areaPx: function (pts) {
      var sum = 0;
      for (var i = 0; i < 4; i++) {
        var a = pts[i], b = pts[(i + 1) % 4];
        sum += a[0] * b[1] - b[0] * a[1];
      }
      return Math.abs(sum);
    },

    isLocked: function (area) {
      return !!area.ratio && !this.unlocked[area.code];
    },

    /**
     * The box height, as a fraction of the picture, that gives a box of
     * fraction-width {@code w} the area's proportions. Fractions are of two
     * different lengths, so the picture's own proportions come into it.
     */
    heightFor: function (area, w) {
      return w * area.ratio * (this.natural.w / this.natural.h);
    },

    /**
     * The shape brought back to the area's proportions: the top edge is
     * kept as it is — its length and its angle — and the sides are rebuilt
     * square to it, as long as the proportions say. What "locked" promises,
     * whatever the shape was turned to.
     */
    proportioned: function (area, r) {
      var pts = this.toPx(r);
      var tl = pts[0], tr = pts[1];
      var ux = tr[0] - tl[0], uy = tr[1] - tl[1];
      var len = Math.sqrt(ux * ux + uy * uy) || 1;
      var side = len * area.ratio;
      // The perpendicular, on the side the shape already extends to.
      var vx = -uy / len * side, vy = ux / len * side;
      var current = [pts[3][0] - tl[0], pts[3][1] - tl[1]];
      if (current[0] * vx + current[1] * vy < 0) {
        vx = -vx;
        vy = -vy;
      }
      return this.fromPx([tl, tr, [tr[0] + vx, tr[1] + vy], [tl[0] + vx, tl[1] + vy]]);
    },

    /** A new, upright shape of the area's proportions when locked, sized to fit. */
    fresh: function (area) {
      var w = 1 / 3, h = 1 / 4;
      if (this.isLocked(area)) {
        h = this.heightFor(area, w);
        if (h > 0.9) {
          h = 0.9;
          w = h / (area.ratio * (this.natural.w / this.natural.h));
        }
      }
      return this.upright((1 - w) / 2, (1 - h) / 2, w, h);
    },

    /** The shape's bounding box, upright, in the area's proportions when locked. */
    straightened: function (area, r) {
      var xs = _.map(r.p, function (c) { return c[0]; }), ys = _.map(r.p, function (c) { return c[1]; });
      var out = this.upright(_.min(xs), _.min(ys), _.max(xs) - _.min(xs), _.max(ys) - _.min(ys));
      return this.isLocked(area) ? this.nudgedInside(this.proportioned(area, out)) : out;
    },

    // ------------------------------------------------------------- render

    serializeData: function () {
      return {
        areas: this.areas,
        views: _.range(1, this.viewCount + 1),
        productPictures: this.productPictures
      };
    },

    onShow: function () {
      // Bound directly: load does not bubble, so a delegated handler never fires.
      this.ui.image.on('load', _.bind(this.onImageLoad, this));
      this.ui.image.on('error', _.bind(function () {
        this.ui.loading.text(_.i18n('pimPositioner.imageFailed', null, 'This picture could not be loaded.'));
      }, this));
      this.bindPointer();
      $(window).on('resize.pimPositioner', _.bind(this.refit, this));
      $(document).on('keydown.pimPositioner', _.bind(this.onKeydown, this));
      this.ui.stage.on('wheel', _.bind(this.onWheel, this));
      this.renderHistory();
      this.select(this.current);
    },

    /** The modal only has its size once shown; a picture fitted before was fitted to nothing. */
    onShown: function () {
      this.refit();
    },

    select: function (selection) {
      this.current = selection;
      this.natural = null;
      this.renderTabs();
      this.renderStrip();
      this.renderScope();
      this.renderReferences();
      var url = this.currentUrl();
      if (!url) {
        this.ui.image.attr('src', '');
        this.ui.overlay.empty();
        return;
      }
      this.ui.loading.text(_.i18n('common.loading', null, 'Loading…')).show();
      this.ui.overlay.empty();
      if (this.ui.image.attr('src') === url && this.ui.image[0].complete && this.ui.image[0].naturalWidth) {
        this.onImageLoad();
      } else {
        this.ui.image.attr('src', url);
      }
    },

    onImageLoad: function () {
      var img = this.ui.image[0];
      if (!img.naturalWidth) {
        return;
      }
      this.natural = {w: img.naturalWidth, h: img.naturalHeight};
      this.ui.loading.hide();
      this.refit();
    },

    /**
     * "Fits the window": as wide as the stage or as tall as it may be,
     * whichever comes first, never larger than the picture itself. Set
     * outright — a picture inside a box sized by the picture cannot be told
     * to fit that box in CSS alone. Done again when the modal is shown and
     * when the window changes size: a picture that loaded before the modal
     * was on screen was measured against nothing.
     */
    refit: function () {
      if (!this.natural) {
        return;
      }
      var stage = this.ui.stage[0];
      if (!stage.clientWidth) {
        return;
      }
      var maxW = stage.clientWidth - 2;
      var maxH = Math.max(200, Math.round(window.innerHeight * 0.62) - 2);
      var scale = Math.min(maxW / this.natural.w, maxH / this.natural.h, 1);
      this.fitWidth = Math.max(1, Math.floor(this.natural.w * scale));
      this.ui.image.css('width', Math.round(this.fitWidth * this.zoom));
      this.draw();
    },

    renderTabs: function () {
      var c = this.current;
      this.$('.js-tab').each(function () {
        var $tab = $(this);
        var on = c && ($tab.attr('data-kind') === c.kind) &&
          (c.kind === 'product' || parseInt($tab.attr('data-view'), 10) === c.view);
        $tab.toggleClass('is-on', !!on);
      });
    },

    /**
     * The pictures the current tab covers: one per option for a view, so the
     * shared placement can be checked on every colour at a glance; the
     * product pictures otherwise.
     */
    renderStrip: function () {
      var $strip = this.ui.strip.empty();
      var c = this.current;
      if (!c) {
        return;
      }
      var items = c.kind === 'product' ?
        _.map(this.productPictures, function (p) {
            return {url: p.url, label: '', key: {kind: 'product', url: p.url}, own: false};
          }) :
        _.compact(_.map(this.options_, _.bind(function (option) {
            var picture = option.pictures[c.view - 1];
            if (!picture) {
              return null;
            }
            return {
              url: picture.url, label: option.code, own: this.hasOverride(option.pimId, c.view),
              key: {kind: 'view', view: c.view, optionPimId: option.pimId}
            };
          }, this)));

      _.each(items, _.bind(function (item) {
        var selected = c.kind === 'product' ? item.key.url === c.url : item.key.optionPimId === c.optionPimId;
        var $thumb = $('<div class="positioner__thumb js-thumb"></div>')
          .toggleClass('is-on', selected)
          .toggleClass('is-own', item.own)
          .data('key', item.key)
          .attr('title', item.own ?
            _.i18n('pimPositioner.ownPlacement', null, 'Has its own placement') :
            _.i18n('pimPositioner.sharedPlacement', null, 'Uses the shared placement'));
        $('<img alt="">').attr('src', item.url).appendTo($thumb);
        if (item.label) {
          $('<span class="positioner__thumb-label"></span>').text(item.label).appendTo($thumb);
        }
        if (item.own) {
          $('<span class="positioner__thumb-own">●</span>').appendTo($thumb);
        }
        $strip.append($thumb);
      }, this));
    },

    /** What an edit applies to, in one line, and the switch to change that. */
    renderScope: function () {
      var $scope = this.ui.scope.empty();
      var c = this.current;
      if (!c) {
        return;
      }
      if (c.kind === 'product') {
        $scope.append($('<div class="positioner__scope-line"></div>').text(
          _.i18n('pimPositioner.scopeProduct', null, 'This picture only.')));
        if (this.inherited['product:' + c.url]) {
          $scope.append(this.inheritedNote());
        }
        return;
      }
      var option = _.findWhere(this.options_, {pimId: c.optionPimId});
      var own = this.hasOverride(c.optionPimId, c.view);
      var others = _.filter(this.options_, _.bind(function (o) {
        return o.pimId !== c.optionPimId && o.pictures[c.view - 1] && !this.hasOverride(o.pimId, c.view);
      }, this)).length;

      $scope.append($('<div class="positioner__scope-line"></div>').text(own ?
        _.i18n('pimPositioner.scopeOwn', null, 'Placement for {option} only.').replace('{option}', option.code) :
        _.i18n('pimPositioner.scopeShared', null, 'Shared placement for picture {n}: applies to {count} other option(s).')
            .replace('{n}', c.view).replace('{count}', others)));

      var $label = $('<label class="positioner__custom"></label>');
      $('<input type="checkbox" class="js-custom">').prop('checked', own).appendTo($label);
      $('<span></span>').text(
        _.i18n('pimPositioner.customFor', null, 'Custom placement for {option}').replace('{option}', option.code))
        .appendTo($label);
      $scope.append($label);

      if (!own && this.inherited['view:' + c.view]) {
        $scope.append(this.inheritedNote());
      }
    },

    /**
     * The supplier's own picture of each print position, for the colour on
     * screen: a thumbnail on the area's row, and the one chosen enlarged
     * below the list. Not drawn on — the supplier's photo is framed its own
     * way — but it says where the print goes better than a code does.
     */
    referenceFor: function (area) {
      var pictures = area.printPictures || [];
      if (!pictures.length) {
        return null;
      }
      var option = this.current && this.current.kind === 'view' ?
        _.findWhere(this.options_, {pimId: this.current.optionPimId}) : null;
      var own = option ? _.findWhere(pictures, {optionCode: option.code}) : null;
      return (own || pictures[0]).url;
    },

    renderReferences: function () {
      _.each(this.areas, _.bind(function (area) {
        var url = this.referenceFor(area);
        var $ref = this.$('.js-area[data-code="' + area.code + '"] .js-ref');
        if (url) {
          $ref.attr('src', url).show();
        } else {
          $ref.hide();
        }
      }, this));
      // The enlarged one follows the colour too.
      if (this.previewCode) {
        this.showPreview(this.previewCode);
      }
    },

    showPreview: function (code) {
      var area = _.findWhere(this.areas, {code: code});
      var url = area ? this.referenceFor(area) : null;
      var $preview = this.$('.js-preview');
      if (!url) {
        this.previewCode = null;
        $preview.hide();
        return;
      }
      this.previewCode = code;
      $preview.find('.js-preview-img').attr('src', url);
      $preview.find('.js-preview-title').text(
        _.i18n('pimPositioner.refTitle', null, 'Print position {area} as the supplier shows it').replace('{area}', code));
      $preview.show();
    },

    onRefClick: function (event) {
      event.stopPropagation();
      var code = $(event.currentTarget).closest('.js-area').attr('data-code');
      // Same thumbnail again puts it away.
      this.showPreview(this.previewCode === code ? null : code);
    },

    onPreviewClick: function () {
      this.showPreview(null);
    },

    inheritedNote: function () {
      return $('<div class="positioner__inherited"></div>').text(
        _.i18n('pimPositioner.inherited', null, 'Starting from the supplier\'s placement. Saving makes it yours.'));
    },

    draw: function () {
      if (!this.natural || !this.current) {
        return;
      }
      var W = this.ui.image.width(), H = this.ui.image.height();
      var rects = this.rects();
      var svg = this.ui.overlay[0];
      $(svg).empty().attr({width: W, height: H, viewBox: '0 0 ' + W + ' ' + H});

      _.each(this.areas, _.bind(function (area) {
        var r = rects[area.code];
        var $row = this.$('.js-area[data-code="' + area.code + '"]').toggleClass('is-placed', !!r);
        $row.find('.js-lock')
          .toggleClass('is-locked mdi-action-lock', this.isLocked(area))
          .toggleClass('mdi-action-lock-open', !this.isLocked(area));
        if (!r) {
          $row.find('.js-state').text('');
          return;
        }
        var pts = this.toPx(r);
        var edge = function (a, b) { return Math.sqrt(Math.pow(b[0] - a[0], 2) + Math.pow(b[1] - a[1], 2)); };
        var sx = this.natural.w / W;
        $row.find('.js-state').text(Math.round(edge(pts[0], pts[1]) * sx) + '×' + Math.round(edge(pts[0], pts[3]) * sx));

        svg.appendChild(this.shape(area, pts));
      }, this));
    },

    /**
     * One placed area, as SVG: the outline, a handle on each corner to pull
     * it out of square, a handle past the top edge to turn it, a handle on
     * the bottom-right corner to resize it, and a label with remove and
     * straighten.
     */
    shape: function (area, pts) {
      var ns = 'http://www.w3.org/2000/svg';
      var el = function (name, attrs) {
        var node = document.createElementNS(ns, name);
        _.each(attrs, function (v, k) { node.setAttribute(k, v); });
        return node;
      };
      var g = el('g', {'class': 'positioner__quad', 'data-code': area.code});
      var points = _.map(pts, function (c) { return c[0] + ',' + c[1]; }).join(' ');

      g.appendChild(el('polygon', {
        points: points, 'data-role': 'body', 'class': 'positioner__body',
        stroke: area.colour, fill: area.colour, 'fill-opacity': 0.12
      }));

      // Turn: a handle a little way out from the middle of the top edge,
      // tied to it, so the grab reads as "turn around the middle".
      var top = [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2];
      var centre = [(pts[0][0] + pts[2][0]) / 2, (pts[0][1] + pts[2][1]) / 2];
      var dx = top[0] - centre[0], dy = top[1] - centre[1];
      var len = Math.sqrt(dx * dx + dy * dy) || 1;
      var grip = [top[0] + dx / len * 22, top[1] + dy / len * 22];
      g.appendChild(el('line', {x1: top[0], y1: top[1], x2: grip[0], y2: grip[1], stroke: area.colour, 'class': 'positioner__tether'}));
      g.appendChild(el('circle', {cx: grip[0], cy: grip[1], r: 6, 'data-role': 'rotate', 'class': 'positioner__rotate', stroke: area.colour}));

      _.each(pts, function (c, i) {
        g.appendChild(el('rect', {
          x: c[0] - 4, y: c[1] - 4, width: 8, height: 8, 'data-role': 'corner', 'data-i': i,
          'class': 'positioner__corner', stroke: area.colour
        }));
      });
      // Resize sits on the bottom-right corner, over its deform handle.
      g.appendChild(el('rect', {
        x: pts[2][0] - 6, y: pts[2][1] - 6, width: 12, height: 12, 'data-role': 'scale',
        'class': 'positioner__scale', fill: area.colour
      }));

      // The label, at the top-left corner, with remove and straighten.
      var label = el('g', {'class': 'positioner__label', transform: 'translate(' + pts[0][0] + ',' + (pts[0][1] - 20) + ')'});
      var width = area.code.length * 7 + 12;
      label.appendChild(el('rect', {x: 0, y: 0, width: width + 36, height: 16, fill: area.colour, rx: 2}));
      var text = el('text', {x: 6, y: 12, 'class': 'positioner__label-text'});
      text.textContent = area.code;
      label.appendChild(text);
      var straighten = el('text', {x: width + 4, y: 12, 'data-role': 'straighten', 'class': 'positioner__label-btn'});
      straighten.textContent = '▭';
      straighten.appendChild(el('title')).textContent = _.i18n('pimPositioner.straighten', null, 'Straighten');
      label.appendChild(straighten);
      var remove = el('text', {x: width + 22, y: 12, 'data-role': 'remove', 'class': 'positioner__label-btn'});
      remove.textContent = '×';
      remove.appendChild(el('title')).textContent = _.i18n('common.remove', null, 'Remove');
      label.appendChild(remove);
      g.appendChild(label);
      return g;
    },

    // ----------------------------------------------------------- gestures

    onTab: function (event) {
      var $tab = $(event.currentTarget);
      if ($tab.attr('data-kind') === 'product') {
        this.select({kind: 'product', url: this.productPictures[0].url});
      } else {
        var view = parseInt($tab.attr('data-view'), 10);
        var option = _.find(this.options_, function (o) { return o.pictures[view - 1]; });
        this.select({kind: 'view', view: view, optionPimId: option.pimId});
      }
    },

    onThumb: function (event) {
      this.select($(event.currentTarget).data('key'));
    },

    /**
     * Custom on: this option gets its own copy of what it shows now, and
     * edits stop reaching the others. Off: the copy goes, the shared
     * placement shows again.
     */
    onCustomChange: function (event) {
      var c = this.current;
      if (!c || c.kind !== 'view') {
        return;
      }
      this.commit();
      if (event.currentTarget.checked) {
        this.overrides[c.optionPimId] = this.overrides[c.optionPimId] || {};
        var copy = {};
        _.each(this.shared[c.view] || {}, function (r, code) {
          copy[code] = {p: _.map(r.p, function (pt) { return pt.slice(); })};
        });
        this.overrides[c.optionPimId][c.view] = copy;
      } else if (this.overrides[c.optionPimId]) {
        delete this.overrides[c.optionPimId][c.view];
      }
      this.renderStrip();
      this.renderScope();
      this.draw();
    },

    onAreaClick: function (event) {
      if (!this.natural) {
        return;
      }
      var code = $(event.currentTarget).attr('data-code');
      var area = _.findWhere(this.areas, {code: code});
      var rects = this.rects();
      if (!rects[code]) {
        // Dropped in the middle, a third of the picture wide: somewhere to
        // start dragging from, not a guess at where the print goes.
        this.commit();
        rects[code] = this.fresh(area);
      }
      this.draw();
    },

    onLockClick: function (event) {
      event.stopPropagation();
      var code = $(event.currentTarget).closest('.js-area').attr('data-code');
      var area = _.findWhere(this.areas, {code: code});
      this.unlocked[code] = !this.unlocked[code];
      // Locking again brings the shape back to the area's proportions, so
      // what is shown is what "locked" promises.
      var rects = this.rects();
      if (!this.unlocked[code] && rects[code]) {
        this.commit();
        rects[code] = this.nudgedInside(this.proportioned(area, rects[code]));
      }
      this.draw();
    },

    /**
     * One pointer at a time, on the overlay: mousedown reads which handle
     * was taken — the body to move, a corner to pull, the top grip to turn,
     * the bottom-right grip to resize — mousemove on the document follows
     * it even when the pointer leaves the picture, mouseup lets go.
     *
     * All of it in the pixels the picture is shown at: turning is a
     * geometric turn, which fractions of two different lengths are not.
     */
    bindPointer: function () {
      var that = this;
      this.ui.overlay.on('mousedown', '[data-role]', function (event) {
        var role = $(this).attr('data-role');
        var code = $(this).closest('.positioner__quad').attr('data-code');
        var area = _.findWhere(that.areas, {code: code});
        var rects = that.rects();
        if (!rects[code]) {
          return;
        }
        if (role === 'remove') {
          that.commit();
          delete rects[code];
          that.draw();
          return;
        }
        if (role === 'straighten') {
          that.commit();
          rects[code] = that.straightened(area, rects[code]);
          that.draw();
          return;
        }
        event.preventDefault();
        // Remembered now, kept only if the hand actually moved something.
        var before = that.snapshot();

        var start = that.toPx(rects[code]);
        var corner = parseInt($(this).attr('data-i'), 10);
        var overlay = that.ui.overlay.offset();
        var origin = [event.pageX - overlay.left, event.pageY - overlay.top];
        var centre = [(start[0][0] + start[2][0]) / 2, (start[0][1] + start[2][1]) / 2];
        var originAngle = Math.atan2(origin[1] - centre[1], origin[0] - centre[0]);

        var move = function (e) {
          var now = [e.pageX - overlay.left, e.pageY - overlay.top];
          var dx = now[0] - origin[0], dy = now[1] - origin[1];
          var pts;

          if (role === 'body') {
            pts = _.map(start, function (c) { return [c[0] + dx, c[1] + dy]; });
            rects[code] = that.nudgedInside(that.fromPx(pts));
          } else if (role === 'corner') {
            pts = _.map(start, function (c) { return c.slice(); });
            pts[corner] = [now[0], now[1]];
            var pulled = that.fromPx(pts);
            if (that.areaPx(pts) > 0) {
              // A corner may be pulled anywhere on the picture, never off it.
              pulled.p[corner] = [Math.max(0, Math.min(1, pulled.p[corner][0])), Math.max(0, Math.min(1, pulled.p[corner][1]))];
              rects[code] = pulled;
            }
          } else if (role === 'rotate') {
            var angle = Math.atan2(now[1] - centre[1], now[0] - centre[0]) - originAngle;
            var cos = Math.cos(angle), sin = Math.sin(angle);
            pts = _.map(start, function (c) {
              var x = c[0] - centre[0], y = c[1] - centre[1];
              return [centre[0] + x * cos - y * sin, centre[1] + x * sin + y * cos];
            });
            var turned = that.fromPx(pts);
            if (that.inside(turned)) {
              rects[code] = turned;
            }
          } else if (role === 'scale') {
            rects[code] = that.scaledFrom(area, start, dx, dy) || rects[code];
          }
          that.draw();
        };
        var up = function () {
          $(document).off('mousemove', move).off('mouseup', up);
          if (that.snapshot() !== before) {
            that.commit(before);
          }
        };
        $(document).on('mousemove', move).on('mouseup', up);
      });
    },

    /**
     * The shape resized from its top-left corner, along its own edges: a
     * turned shape grows along its turned sides. Each corner is placed by
     * how far along the top edge and the left edge it sits, and those two
     * lengths are what the drag changes. Locked, both change alike.
     */
    scaledFrom: function (area, start, dx, dy) {
      var tl = start[0];
      var u = [start[1][0] - tl[0], start[1][1] - tl[1]];
      var v = [start[3][0] - tl[0], start[3][1] - tl[1]];
      var det = u[0] * v[1] - u[1] * v[0];
      if (Math.abs(det) < 1e-6) {
        return null;
      }
      var uLen = Math.sqrt(u[0] * u[0] + u[1] * u[1]), vLen = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
      // The drag, projected on each edge's direction.
      var du = (dx * u[0] + dy * u[1]) / uLen, dv = (dx * v[0] + dy * v[1]) / vLen;
      var sx = Math.max(0.05, (uLen + du) / uLen), sy = Math.max(0.05, (vLen + dv) / vLen);
      if (this.isLocked(area)) {
        // Whichever way the hand moves further sets the size.
        sx = sy = Math.abs(du) >= Math.abs(dv) ? sx : sy;
      }
      var pts = _.map(start, function (c) {
        var px = c[0] - tl[0], py = c[1] - tl[1];
        // Coordinates of the corner in the (u, v) basis.
        var a = (px * v[1] - py * v[0]) / det, b = (u[0] * py - u[1] * px) / det;
        return [tl[0] + a * sx * u[0] + b * sy * v[0], tl[1] + a * sx * u[1] + b * sy * v[1]];
      });
      var out = this.fromPx(pts);
      return this.inside(out) ? out : null;
    },

    // --------------------------------------------------------------- save

    /**
     * Everything, in the PIM's shape. A shared placement is written in the
     * pixels of the first option's picture for that view; each override in
     * its own picture's; each product picture in its own. Sizes come from
     * what the browser measured when the picture was shown, and pictures
     * never shown in this session are measured now.
     */
    entries: function () {
      var deferreds = [];
      var entries = [];
      var that = this;

      var push = function (url, view, optionPimId, rects) {
        if (!url) {
          return;
        }
        deferreds.push(that.measure(url).then(function (natural) {
          entries.push({
            imageUrl: url, view: view, optionPimId: optionPimId,
            positions: that.toPositions(rects, natural)
          });
        }));
      };

      _.each(this.shared, function (rects, view) {
        view = parseInt(view, 10);
        var option = _.find(that.options_, function (o) { return o.pictures[view - 1]; });
        push(option ? option.pictures[view - 1].url : null, view, null, rects);
      });
      _.each(this.overrides, function (byView, optionPimId) {
        var option = _.findWhere(that.options_, {pimId: optionPimId});
        _.each(byView, function (rects, view) {
          view = parseInt(view, 10);
          push(option && option.pictures[view - 1] ? option.pictures[view - 1].url : null, view, optionPimId, rects);
        });
      });
      _.each(this.product, function (rects, url) {
        push(url, null, null, rects);
      });

      return $.when.apply($, deferreds).then(function () {
        return entries;
      });
    },

    measure: function (url) {
      var defer = $.Deferred();
      var img = new Image();
      img.onload = function () {
        defer.resolve({w: img.naturalWidth, h: img.naturalHeight});
      };
      img.onerror = function () {
        defer.reject();
      };
      img.src = url;
      return defer.promise();
    },

    onSave: function () {
      this.ui.save.prop('disabled', true);
      this.ui.error.text('');
      this.entries()
        .then(_.bind(function (entries) {
          this.trigger('save', entries, _.bind(function (failed) {
            if (this.isDestroyed) {
              return;
            }
            if (failed) {
              this.ui.save.prop('disabled', false);
              this.ui.error.text(_.i18n('pimPositioner.saveFailed', null, 'The placement was not saved. Try again.'));
            } else {
              this.hide();
            }
          }, this));
        }, this))
        .fail(_.bind(function () {
          this.ui.save.prop('disabled', false);
          this.ui.error.text(_.i18n('pimPositioner.imageFailed', null, 'This picture could not be loaded.'));
        }, this));
    },

    onDestroy: function () {
      $(window).off('resize.pimPositioner');
      $(document).off('keydown.pimPositioner');
      this.ui.stage.off('wheel');
      this.ui.overlay.off('mousedown');
      this.ui.image.off('load error');
    }
  });
});

